import { useQuery } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { BackgroundProcess, FileData } from 'src/data-capture/hooks/file-uploads/types';

type SuitabilityReportType = 'NO_CHANGE';

interface BaseDocument {
  id: string;
  clientId: string;
  createdAt: string;
  fileId?: number;
  file?: FileData;
  backgroundProcess?: BackgroundProcess;
}

interface SuitabilityReportDocument extends BaseDocument {
  documentType: 'suitability-report';
  reportType?: SuitabilityReportType;
}

interface MeetingAgendaDocument extends BaseDocument {
  documentType: 'meeting-agenda';
}

interface DataExportDocument extends BaseDocument {
  documentType: 'data-export';
  configDisplayName?: string;
}

interface MeetingNoteDocument extends BaseDocument {
  documentType: 'meeting-note';
}

interface DataUploadDocument extends BaseDocument {
  documentType: 'data-upload';
  fileName: string;
  dataUploadModelId?: string;
}

export type UnifiedDocument =
  | SuitabilityReportDocument
  | MeetingAgendaDocument
  | DataExportDocument
  | MeetingNoteDocument
  | DataUploadDocument;

type UseAllClientDocumentsOptions =
  | {
      type?: 'client';
      clientId?: string;
    }
  | {
      type: 'family';
      familyId: string;
    };

type UseAllClientDocumentsReturn = {
  documents: UnifiedDocument[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
};

export const useAllClientDocuments = (
  options: UseAllClientDocumentsOptions = { type: 'client' },
): UseAllClientDocumentsReturn => {
  const { clientId: defaultClientId } = useCurrentClient();

  const queryKey =
    options.type === 'family'
      ? ['families', options.familyId, 'all-documents']
      : ['clients', options.clientId ?? defaultClientId, 'all-documents'];

  const enabled =
    options.type === 'family' ? !!options.familyId : !!(options.clientId ?? defaultClientId);

  const { data, isLoading, isError, error, refetch } = useQuery<UnifiedDocument[], Error>({
    queryKey,
    enabled,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const documents = data || [];

  return {
    documents,
    isLoading,
    isError,
    error,
    refetch,
  };
};
